/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib components
import { Stack, Text, Center, Group, Button } from '@mantine/core';

// UI local component
import GroupCheckbox from '@features/Group/GroupCheckbox';

// Types
import { QuestionGroupPickerProps } from './types';

// Styles
import * as classes from './styles/index.css';
import { palette } from '@app/shared/ui-kit/colors';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function QuestionGroupPicker({
  groups,
  selectedGroups,
  withoutTitle = false,
  variant = 'grid',
  allGroupsSelected,
  onToggleGroup,
  ...props
}: QuestionGroupPickerProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation('Support');

  /* ******************************** CALLBACKS ******************************* */

  const selectAllGroups = () => {
    const deselectedGroups = groups.filter(
      (group) =>
        !selectedGroups.some((selectedGroup) => selectedGroup.id === group.id),
    );
    onToggleGroup(deselectedGroups.map((group) => ({ group, selected: true })));
  };

  const deselectAllGroups = () => {
    onToggleGroup(selectedGroups.map((group) => ({ group, selected: false })));
  };

  /* ******************************** RENDERING ******************************* */

  return groups.length === 0 ? (
    <Center w="100%">
      <Center
        p="xs"
        c={palette.blackroomLightGray5}
        fz="sm"
        fw="bold"
        w="100%"
        maw={240}
        ta="center"
      >
        {t('QuestionGroupPicker.noGroups')}
      </Center>
    </Center>
  ) : (
    <Stack align="stretch" gap={5}>
      <Group justify="space-between" py={5} px={10}>
        <Text fw="bold" c={palette.blackroomDarkGray4} fz="sm">
          {!withoutTitle && t('QuestionGroupPicker.groups')}
        </Text>
        <Button
          variant="transparent"
          c={allGroupsSelected ? 'blackroomRed' : 'blackroomBlue'}
          td="underline"
          fw={300}
          onClick={() =>
            allGroupsSelected ? deselectAllGroups() : selectAllGroups()
          }
        >
          {allGroupsSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </Group>

      <Stack
        className={`${classes.questionGroupPicker} ${classes.questionGroupPickerVariant[variant]}`}
        {...props}
      >
        {groups.map((group) => (
          <GroupCheckbox
            key={group.id}
            group={group}
            active={selectedGroups.some(
              (selectedGroup) => selectedGroup.id === group.id,
            )}
            onToggleGroup={(group, selected) =>
              onToggleGroup([{ group, selected }])
            }
          />
        ))}
      </Stack>
    </Stack>
  );
}

export default QuestionGroupPicker;
